'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Button } from '@startengine/design-system.ui.button';
import { Tombstone } from '@startengine/design-system.ui.tombstone';
import { Main404Text, MainBox, RedirectText } from './not-found.styles';
import Link from 'next/link';
import useGetElasticOfferingsInfinite from 'hooks/search/useGetElasticOfferingsInfinite';

const Tombstones = dynamic(() => import('components/Tombstones'), {
  ssr: false,
});

const NotFoundClient = () => {
  const [countdown, setCountdown] = useState(5);
  const router = useRouter();

  // Fetch offerings to show while user waits
  const { data: elasticOfferingsData, isFetching } = useGetElasticOfferingsInfinite({
    query: '',
    size: 3, // Show only 3 offerings
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          router.push('/explore');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [router]);

  const handleExploreNow = () => {
    router.push('/explore');
  };

  const offerings = elasticOfferingsData?.pages?.[0]?.list || [];

  return (
    <Grid
      data-testid="grid-container"
      data-cy="grid-container"
      sx={{
        my: {
          xs: '80px',
          md: '120px',
        },
        px: {
          xs: 2,
          lg: 0,
        },
      }}
    >
      <MainBox>
        <Main404Text>404</Main404Text>
        <Typography
          variant="p1Medium"
          align="center"
          sx={{
            maxWidth: '400px',
            mb: 2,
          }}
        >
          The startup page you&apos;re looking for doesn&apos;t exist or has been moved.
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{
            maxWidth: '400px',
            mb: 3,
            color: 'text.secondary',
          }}
        >
          Don&apos;t worry! Discover amazing investment opportunities on our explore page.
        </Typography>

        <RedirectText variant="body2" align="center" data-cy="redirect-countdown">
          Redirecting to explore page in {countdown} {countdown === 1 ? 'second' : 'seconds'}...
        </RedirectText>

        <Button
          variant="contained"
          onClick={handleExploreNow}
          data-cy="explore-now-button"
          sx={{
            height: '48px',
            width: '327px',
            mt: 2,
          }}
        >
          Explore Startups Now
        </Button>

        <Link href="/" style={{ marginTop: '16px' }}>
          <Button
            variant="outlined"
            data-cy="go-home-button"
            sx={{
              height: '48px',
              width: '327px',
            }}
          >
            Go Back Home
          </Button>
        </Link>
      </MainBox>

      {/* Show offerings while user waits */}
      <Box
        sx={{
          mt: 8,
          mb: 4,
          maxWidth: '1200px',
          mx: 'auto',
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 600,
          }}
          data-cy="explore-offerings-title"
        >
          Explore Investment Opportunities
        </Typography>

        {isFetching && (
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Tombstone loading imgHeight={192} imgWidth={344} />
              </Grid>
            ))}
          </Grid>
        )}
        {!isFetching && offerings.length > 0 && (
          <Grid container spacing={3} justifyContent="center" data-cy="404-offerings-grid">
            <Tombstones offerings={offerings} />
          </Grid>
        )}
      </Box>
    </Grid>
  );
};

export default NotFoundClient;
